import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { Member, MemberFilters, MemberStatsData } from '../types/memberships';

interface PackageOption {
  id: number;
  package_name: string;
  price: number;
  duration: number;
  duration_type: 'day' | 'week' | 'month' | 'year';
  duration_months: number;
}

interface MembersDataResponse {
  members: Member[];
  stats: MemberStatsData;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  packages: PackageOption[];
}

export function useMembersData(
  venueSlug: string | undefined,
  filters: MemberFilters
) {
  return useQuery<MembersDataResponse, Error>({
    queryKey: ['venue-members', venueSlug, filters.status, filters.search, filters.sort, filters.page],

    queryFn: async () => {
      if (!venueSlug) throw new Error('Venue slug is required');

      // Fetch members, stats, and packages in parallel
      const [membersRes, statsRes, packagesRes] = await Promise.all([
        vendorApi.getVenueMembers(venueSlug, {
          status: filters.status === 'all' ? undefined : filters.status,
          search: filters.search.trim() || undefined,
          sort: filters.sort || 'recent',
          page: filters.page,
          per_page: 20,
        }),
        vendorApi.getVenueMemberStats(venueSlug),
        vendorApi.getMembershipPackages(venueSlug),
      ]);

      const rawPackages = packagesRes.data?.packages || [];
      const packages: PackageOption[] = rawPackages
        .filter((p: any) => p.is_active)
        .map((p: any) => ({
          id: p.id,
          package_name: p.package_name,
          price: parseFloat(p.price) || 0,
          duration: p.duration || p.duration_months || 1,
          duration_type: p.duration_type || 'month',
          duration_months: p.duration_months || 1,
        }));

      return {
        members: membersRes.data?.members || [],
        stats: statsRes.data || {
          active_members: 0,
          total_members: 0,
          expiring_soon: 0,
          total_revenue: 0,
          venue_earnings: 0,
        },
        pagination: membersRes.data?.pagination || {
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: 0,
        },
        packages,
      };
    },

    enabled: !!venueSlug,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });
}
